import { useState, useMemo } from 'react';
import type { AvailabilitySlot, Appointment, AppointmentType } from '../../../mocks/appointments';
import { calculateFranjaOccupancy } from '../../../utils/occupancy';

const monthNames = [
  'Enero','Febrero','Marzo','Abril','Mayo','Junio',
  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre',
];
const dayNamesShort = ['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'];

function formatDateISO(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth()+1).padStart(2,'0');
  const day = String(d.getDate()).padStart(2,'0');
  return `${y}-${m}-${day}`;
}

function isSameDay(d1: Date, d2: Date) {
  return d1.getFullYear()===d2.getFullYear() && d1.getMonth()===d2.getMonth() && d1.getDate()===d2.getDate();
}

interface HourlyOccupancyProps {
  appointments: Appointment[];
  slots: AvailabilitySlot[];
  types: AppointmentType[];
  blockedDates: { date: string }[];
}

export default function HourlyOccupancy({ appointments, slots, types, blockedDates }: HourlyOccupancyProps) {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [hovered, setHovered] = useState<{
    day: Date;
    franja: { start: string; end: string; maxBookings: number; booked: number; pct: number };
  } | null>(null);

  const daysInMonth = useMemo(() => new Date(currentYear, currentMonth+1, 0).getDate(), [currentMonth, currentYear]);
  const firstDayOfMonth = useMemo(() => new Date(currentYear, currentMonth, 1).getDay(), [currentMonth, currentYear]);

  const calendarDays = useMemo(() => {
    const days: (Date|null)[] = [];
    const offset = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
    for (let i=0;i<offset;i++) days.push(null);
    for (let i=1;i<=daysInMonth;i++) days.push(new Date(currentYear, currentMonth, i));
    return days;
  }, [firstDayOfMonth, daysInMonth, currentMonth, currentYear]);

  const goPrev = () => {
    if (currentMonth===0){setCurrentMonth(11);setCurrentYear(y=>y-1);}
    else setCurrentMonth(m=>m-1);
  };
  const goNext = () => {
    if (currentMonth===11){setCurrentMonth(0);setCurrentYear(y=>y+1);}
    else setCurrentMonth(m=>m+1);
  };

  const getBarColor = (pct: number, isFull: boolean) => {
    if (isFull) return 'bg-red-500';
    if (pct === 0) return 'bg-emerald-200';
    if (pct < 50) return 'bg-emerald-400';
    if (pct < 75) return 'bg-amber-300';
    return 'bg-orange-400';
  };

  const getBarHeight = (pct: number) => {
    return Math.max(12, Math.min(100, pct > 0 ? (pct / 100) * 80 + 12 : 12));
  };

  return (
    <div className="space-y-6">
      <h2 className="font-serif text-brand-black text-2xl font-medium">
        Ocupación por franjas
      </h2>
      <p className="font-sans text-brand-gray-500 text-sm">
        Cada día muestra sus franjas horarias como barras agrupadas. Verde = libre, amarillo/naranja = ocupado, rojo = completo.
      </p>

      <div className="bg-white border border-brand-gray-200 rounded-2xl p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <button onClick={goPrev} className="w-10 h-10 rounded-full bg-brand-gray-50 border border-brand-gray-200 flex items-center justify-center text-brand-black hover:border-brand-black transition-colors">
            <i className="ri-arrow-left-s-line text-lg" />
          </button>
          <h3 className="font-serif text-brand-black text-xl font-medium">{monthNames[currentMonth]} {currentYear}</h3>
          <button onClick={goNext} className="w-10 h-10 rounded-full bg-brand-gray-50 border border-brand-gray-200 flex items-center justify-center text-brand-black hover:border-brand-black transition-colors">
            <i className="ri-arrow-right-s-line text-lg" />
          </button>
        </div>

        {/* Weekday headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNamesShort.map(d=>
            <div key={d} className="text-center font-sans text-brand-gray-400 text-xs uppercase tracking-wider py-2">{d}</div>
          )}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((date, idx)=>{
            if (!date) return <div key={`empty-${idx}`} className="aspect-[4/5]" />;
            const iso = formatDateISO(date);
            const isToday = isSameDay(date, today);
            const isBlocked = blockedDates.some(b=>b.date===iso);
            const dow = date.getDay();
            const franjas = calculateFranjaOccupancy(iso, dow, slots, appointments, types);
            const hasData = franjas.length > 0;
            const dayTotal = franjas.reduce((s, f) => s + f.maxBookings, 0);
            const dayBooked = franjas.reduce((s, f) => s + f.booked, 0);
            const dayPct = dayTotal > 0 ? Math.round((dayBooked / dayTotal) * 100) : 0;

            return (
              <div
                key={iso}
                className={`aspect-[4/5] rounded-xl border p-1.5 flex flex-col justify-between transition-all ${
                  isToday ? 'border-brand-black bg-brand-gray-50' : 'border-brand-gray-100 hover:border-brand-gray-300'
                } ${isBlocked ? 'bg-red-50/40 border-red-100' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <span className={`font-sans text-xs font-medium ${isToday?'text-brand-black':'text-brand-gray-600'}`}>
                    {date.getDate()}
                  </span>
                  {dayTotal > 0 && (
                    <span className={`font-sans text-[10px] font-medium px-1 rounded ${dayPct >= 90 ? 'bg-red-50 text-red-600' : dayPct >= 60 ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`}>
                      {dayBooked}/{dayTotal}
                    </span>
                  )}
                  {isBlocked && <span className="w-1.5 h-1.5 rounded-full bg-red-400" />}
                </div>

                {/* Franja bars */}
                <div className="flex items-end gap-[2px] h-16 mt-auto px-0.5">
                  {hasData ? (
                    franjas.map((franja, i)=>{
                      const hPct = getBarHeight(franja.pct);
                      const isFull = franja.available === 0;
                      return (
                        <div
                          key={i}
                          className="flex-1 flex flex-col justify-end group relative cursor-pointer"
                          onMouseEnter={()=>setHovered({day:date,franja})}
                          onMouseLeave={()=>setHovered(null)}
                        >
                          <div
                            className={`w-full rounded-t-sm ${getBarColor(franja.pct, isFull)}`}
                            style={{ height: `${hPct}%` }}
                          />
                        </div>
                      );
                    })
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="font-sans text-[10px] text-brand-gray-300">—</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 mt-4 pt-4 border-t border-brand-gray-200 flex-wrap">
          <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-200"/><span className="font-sans text-brand-gray-500 text-xs">0%</span></div>
          <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-400"/><span className="font-sans text-brand-gray-500 text-xs">&lt;50%</span></div>
          <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-amber-300"/><span className="font-sans text-brand-gray-500 text-xs">50-75%</span></div>
          <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-orange-400"/><span className="font-sans text-brand-gray-500 text-xs">75-99%</span></div>
          <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-red-500"/><span className="font-sans text-brand-gray-500 text-xs">100%</span></div>
          <div className="flex items-center gap-1.5 ml-auto"><span className="w-2.5 h-2.5 rounded-full bg-red-400"/><span className="font-sans text-brand-gray-500 text-xs">Bloqueado</span></div>
        </div>
      </div>

      {/* Hover tooltip detail */}
      {hovered && (
        <div className="fixed z-50 bg-brand-black text-white rounded-xl px-4 py-3 shadow-xl pointer-events-none"
          style={{
            left: '50%',
            bottom: '24px',
            transform: 'translateX(-50%)',
          }}
        >
          <p className="font-sans text-xs font-medium">
            {hovered.day.getDate()} {monthNames[hovered.day.getMonth()]} — {hovered.franja.start} a {hovered.franja.end}
          </p>
          <p className="font-sans text-[11px] text-white/70 mt-0.5">
            {hovered.franja.booked} / {hovered.franja.maxBookings} reservas · {hovered.franja.maxBookings - hovered.franja.booked} libres
          </p>
        </div>
      )}
    </div>
  );
}